import { CURRENCY_BY_RUNTIME_CORRIDOR, type RuntimeCorridor } from "../constants/dispersions.constants";
import type { PayoutRail } from "../services/paymentsPayout.service";
import type { NativeQuote } from "../services/paymentsQuotes.service";

/**
 * Why a quote cannot back the payout being confirmed, or `null` when it can.
 *
 * `CONSUMED` is deliberately absent. The store only ever holds a quote returned by `POST /quotes`, which is
 * always `ACTIVE` (`getQuote` has no callers), so a consumed quote is unreachable here — the terminal case is
 * handled by clearing the quote, which surfaces as `missing`.
 */
export type PayoutQuoteBlock =
  | null
  | "pricing"
  | "missing"
  | "wrong_service"
  | "expired"
  | "corridor_mismatch";

export interface PayoutQuoteReadinessInput {
  quote: NativeQuote | null;
  /** A re-quote in flight. Blocks: the quote on screen is about to be replaced. */
  isLoading: boolean;
  /** The countdown fired. Necessary but NOT sufficient — see the note on `now`. */
  isExpiredFlag: boolean;
  /**
   * Injected instead of read inside, both to keep the function pure and because the flag alone cannot be
   * trusted: `setTimeout` is throttled in background tabs, so the countdown can lag the real expiry by
   * minutes — exactly when it matters. Whoever calls this must pass the same clock the submit uses, or the
   * two disagree and the guard becomes a second source of truth.
   */
  now: number;
  corridor: RuntimeCorridor | null;
  /**
   * The rail the quote was frozen for, from `railForQuote(corridor, recipientFields)` — never
   * `RAIL_BY_RUNTIME_CORRIDOR`, which answers `BREB` for Colombia and would block every Transfiya payout.
   */
  rail: PayoutRail | null;
}

/**
 * Decides whether a quote is apt to be redeemed by the payout about to be confirmed.
 *
 * Extracted so the step-3 validity rule and the submit guard read the SAME predicate. They used to be one
 * (only the submit had it), which is why the confirm button stayed enabled over a quote that was about to be
 * rejected: the user got an error screen instead of a disabled button.
 *
 * The corridor check mirrors the backend's own (`payout.service.ts` compares `payout_country` AND
 * `payout_rail`, then currency separately). The frontend used to compare only the delivery currency, which
 * COP↔COP passes — so switching Colombia between Bre-B and Transfiya with a stale quote produced a
 * `quote_corridor_mismatch` from the server that nothing here could see coming.
 *
 * Order matches the submit's, so the reason reported is the same one that would have been thrown.
 */
export function resolvePayoutQuoteBlock({
  quote,
  isLoading,
  isExpiredFlag,
  now,
  corridor,
  rail,
}: PayoutQuoteReadinessInput): PayoutQuoteBlock {
  if (isLoading) return "pricing";
  if (!quote) return "missing";
  if (quote.service !== "payout") return "wrong_service";
  if (isExpiredFlag || Date.parse(quote.expires_at) <= now) return "expired";

  // An unresolvable corridor is a broken form, not a quote problem: the submit already refuses it upstream
  // and blocking on it here would report the wrong reason.
  if (!corridor) return null;

  if (quote.payout) {
    return quote.payout.country === corridor && quote.payout.rail === rail ? null : "corridor_mismatch";
  }

  // No corridor leg on the quote: fall back to the delivery currency rather than block. A quote priced
  // without `country` + `rail` is refused by the backend earlier, with `quote_payout_leg_missing` — claiming
  // a corridor mismatch here would be a worse answer than the one the server gives.
  return quote.currency.to === CURRENCY_BY_RUNTIME_CORRIDOR[corridor] ? null : "corridor_mismatch";
}
